import { cva } from "class-variance-authority";

/**
 * Icon Button Variants
 *
 * V&A Waterfront Design System - Icon Button
 * Figma: UI Components / Buttons / Icon
 *
 * Type Variants:
 * - grey: Icon on subtle background (Figma: Icon / Grey)
 *   Uses surface-base-secondary + icon-primary semantic tokens (auto-switch via .dark)
 *   Background-Hover/Disabled use component tokens (same value both modes)
 *   Icon-Disabled switches per mode via component token
 *
 * - blue: Icon on primary colored background (uses button-primary-* tokens)
 *   Same tokens as filled button — auto-switches via .dark
 *
 * Sizes:
 * - default: 44x44px (p-3 = 12px padding, icon 20px)
 * - sm: 32x32px (p-2 = 8px padding, icon 16px)
 */

export const iconButtonVariants = cva(
  // Base styles
  [
    "inline-flex items-center justify-center",
    "rounded-full transition-colors cursor-pointer",
    "disabled:pointer-events-none",
    "data-[disabled]:pointer-events-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "focus:outline-none",
  ],
  {
    variants: {
      variant: {
        // Grey - Default for all surfaces
        // Figma: UI Components / Buttons / Icon / Grey
        grey: [
          "bg-surface-base-secondary text-icon-primary",
          "hover:bg-button-icon-grey-background-hover",
          "focus-visible:ring-2 focus-visible:ring-border-focus",
          "disabled:bg-button-icon-grey-background-disabled disabled:text-button-icon-grey-icon-disabled",
          "data-[disabled]:bg-button-icon-grey-background-disabled data-[disabled]:text-button-icon-grey-icon-disabled",
        ],
        // White - Alias for grey (same tokens, switches via .dark parent)
        white: [
          "bg-surface-base-secondary text-icon-primary",
          "hover:bg-button-icon-grey-background-hover",
          "focus-visible:ring-2 focus-visible:ring-border-focus",
          "disabled:bg-button-icon-grey-background-disabled disabled:text-button-icon-grey-icon-disabled",
          "data-[disabled]:bg-button-icon-grey-background-disabled data-[disabled]:text-button-icon-grey-icon-disabled",
        ],
        // Blue Filled - Primary colored background with inverted icon
        // Reuses button-primary-* tokens (same as filled button)
        blue: [
          "bg-button-primary-background text-button-primary-icon",
          "hover:bg-button-primary-background-hover",
          "focus-visible:ring-2 focus-visible:ring-border-focus",
          "disabled:bg-button-primary-background-disabled disabled:text-button-primary-icon-disabled",
          "data-[disabled]:bg-button-primary-background-disabled data-[disabled]:text-button-primary-icon-disabled",
        ],
      },
      size: {
        // Default - 44x44px (H44)
        default: ["size-11 p-3", "[&_svg]:size-5"],
        // Small - 32x32px (H32)
        sm: ["size-8 p-2", "[&_svg]:size-4"],
      },
    },
    defaultVariants: {
      variant: "grey",
      size: "default",
    },
  },
);
